// src/components/sdk/components/ApiKeyContent.tsx
import React from 'react';
import { Button, Input, Space, Table, Tag } from 'antd';
import { PlusOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { SdkAppData } from '../SdkInterface';
import { storageUtil } from '../utils/StorageUtil';

interface ApiKeyContentProps {
    t: (key: string, options?: any) => string;
    appList: SdkAppData[];
    loading: boolean;
    searchText: string;
    onSearchTextChange: (text: string) => void;
    onSearch: (value: string) => void;
    onRefresh: () => void;
    onCreateApp: () => void;
    onGetAppDetail: (app: SdkAppData) => void;
    onToggleStatus: (record: SdkAppData) => void;
    onDeleteApp: (record: SdkAppData) => void;
    userRole?: number;
    isMobile?: boolean;
}

interface ApiKeyContentState { }

class ApiKeyContent extends React.Component<ApiKeyContentProps, ApiKeyContentState> {
    constructor(props: ApiKeyContentProps) {
        super(props);
        this.state = {};
    }

    // 获取表格列配置
    getColumns = (): ColumnsType<SdkAppData> => {
        const { t, onGetAppDetail, onToggleStatus, onDeleteApp, isMobile } = this.props;
        const isGuest = storageUtil.isGuest();

        // 移动端：只显示三列
        if (isMobile) {
            const mobileColumns: ColumnsType<SdkAppData> = [
                {
                    title: t('sdk.app.name') || '应用名称',
                    dataIndex: 'appName',
                    key: 'appName',
                    render: (text: string, record: SdkAppData) => (
                        <a onClick={() => onGetAppDetail(record)}>
                            {text}
                        </a>
                    ),
                },
                {
                    title: t('sdk.app.status') || '状态',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status: string) => {
                        const statusMap: Record<string, { color: string; text: string }> = {
                            active: { color: 'success', text: t('sdk.status.active') || '运行中' },
                            inactive: { color: 'error', text: t('sdk.status.inactive') || '已停用' },
                            pending: { color: 'warning', text: t('sdk.status.pending') || '审核中' },
                        };
                        const info = statusMap[status];
                        return <Tag color={info?.color}>{info?.text || status}</Tag>;
                    },
                },
                {
                    title: t('sdk.app.remainingQuota') || '剩余次数',
                    dataIndex: 'remainingQuota',
                    key: 'remainingQuota',
                },
            ];

            // 如果不是访客角色，添加操作列
            if (!isGuest) {
                mobileColumns.push({
                    title: t('sdk.app.actions') || '操作',
                    key: 'actions',
                    render: (_: any, record: SdkAppData) => (
                        <Space size={4}>
                            <Button
                                type={record.status === 'active' ? 'default' : 'primary'}
                                size="small"
                                onClick={() => onToggleStatus(record)}
                                style={{ fontSize: 12, padding: '0 8px' }}
                            >
                                {record.status === 'active'
                                    ? (t('sdk.app.disable') || '禁用')
                                    : (t('sdk.app.activate') || '激活')}
                            </Button>
                            <Button
                                type="primary"
                                danger
                                size="small"
                                onClick={() => onDeleteApp(record)}
                                style={{ fontSize: 12, padding: '0 8px' }}
                            >
                                {t('sdk.app.delete') || '删除'}
                            </Button>
                        </Space>
                    ),
                });
            }

            return mobileColumns;
        }

        // 桌面端：显示所有列
        const columns: ColumnsType<SdkAppData> = [
            {
                title: t('sdk.app.name') || '应用名称',
                dataIndex: 'appName',
                key: 'appName',
                render: (text: string, record: SdkAppData) => (
                    <a onClick={() => onGetAppDetail(record)}>
                        {text}
                    </a>
                ),
            },
            {
                title: t('sdk.app.packageName') || '包名',
                dataIndex: 'packageName',
                key: 'packageName',
                responsive: ['md'],
            },
            {
                title: t('sdk.app.platform') || '平台',
                dataIndex: 'platform',
                key: 'platform',
                render: (platform: string) => {
                    const platformMap: Record<string, { color: string; text: string }> = {
                        android: { color: 'green', text: t('sdk.platform.android') || 'Android' },
                        ios: { color: 'blue', text: t('sdk.platform.ios') || 'iOS' },
                    };
                    const info = platformMap[platform?.toLowerCase()];
                    return <Tag color={info?.color}>{info?.text || platform}</Tag>;
                },
                responsive: ['sm'],
            },
            // {
            //     title: t('sdk.app.creator') || '创建者',
            //     dataIndex: 'userName',
            //     key: 'userName',
            //     responsive: ['lg'],
            // },
            {
                title: t('sdk.app.status') || '状态',
                dataIndex: 'status',
                key: 'status',
                render: (status: string) => {
                    const statusMap: Record<string, { color: string; text: string }> = {
                        active: { color: 'success', text: t('sdk.status.active') || '运行中' },
                        inactive: { color: 'error', text: t('sdk.status.inactive') || '已停用' },
                        pending: { color: 'warning', text: t('sdk.status.pending') || '审核中' },
                    };
                    const info = statusMap[status];
                    return <Tag color={info?.color}>{info?.text || status}</Tag>;
                },
            },
            {
                title: t('sdk.app.createTime') || '创建时间',
                dataIndex: 'createTime',
                key: 'createTime',
                responsive: ['lg'],
            },
            {
                title: t('sdk.app.expiryTime') || '过期时间',
                dataIndex: 'endTime',
                key: 'endTime',
                responsive: ['lg'],
            },
            {
                title: t('sdk.app.remainingQuota') || '剩余次数',
                dataIndex: 'remainingQuota',
                key: 'remainingQuota',
                responsive: ['md'],
            },
        ];

        // 如果不是访客角色，添加操作列
        if (!isGuest) {
            columns.push({
                title: t('sdk.app.actions') || '操作',
                key: 'actions',
                width: 180,
                render: (_: any, record: SdkAppData) => (
                    <Space size={8}>
                        <Button
                            type={record.status === 'active' ? 'default' : 'primary'}
                            size="small"
                            onClick={() => onToggleStatus(record)}
                        >
                            {record.status === 'active'
                                ? (t('sdk.app.disable') || '禁用')
                                : (t('sdk.app.activate') || '激活')}
                        </Button>
                        <Button
                            type="primary"
                            danger
                            size="small"
                            onClick={() => onDeleteApp(record)}
                        >
                            {t('sdk.app.delete') || '删除'}
                        </Button>
                    </Space>
                ),
            });
        }

        return columns;
    };

    // 过滤应用列表
    getFilteredAppList = (): SdkAppData[] => {
        const { appList, searchText } = this.props;
        if (!searchText || !searchText.trim()) {
            return appList;
        }
        const lower = searchText.toLowerCase().trim();
        return appList.filter(item =>
            item.appName?.toLowerCase().includes(lower) ||
            item.packageName?.toLowerCase().includes(lower)
        );
    };

    render() {
        const {
            t,
            loading,
            searchText,
            onSearchTextChange,
            onSearch,
            onRefresh,
            onCreateApp,
            isMobile,
        } = this.props;

        const filteredAppList = this.getFilteredAppList();
        const isGuest = storageUtil.isGuest();

        return (
            <>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                    flexWrap: 'nowrap',
                    gap: 8,
                }}>
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'nowrap',
                        flex: 1,
                        alignItems: 'center',
                        minWidth: 0,
                    }}>
                        <Input.Search
                            placeholder={t('sdk.search.placeholder') || '搜索应用名称或包名'}
                            style={{
                                width: isMobile ? 120 : 280,
                                flex: isMobile ? 1 : undefined,
                                minWidth: isMobile ? 80 : undefined,
                            }}
                            value={searchText}
                            onChange={(e) => {
                                onSearchTextChange(e.target.value);
                                onSearch(e.target.value);
                            }}
                            enterButton={<SearchOutlined />}
                            onSearch={onSearch}
                            size={isMobile ? 'small' : 'middle'}
                        />
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={onRefresh}
                            loading={loading}
                            size={isMobile ? 'small' : 'middle'}
                            style={{ flexShrink: 0 }}
                        >
                            {!isMobile && (t('sdk.refresh') || '刷新')}
                        </Button>
                        {!isGuest && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={onCreateApp}
                                size={isMobile ? 'small' : 'middle'}
                                style={{ flexShrink: 0 }}
                            >
                                {!isMobile && (t('sdk.createApp') || '创建应用')}
                            </Button>
                        )}
                    </div>
                </div>

                <Table
                    columns={this.getColumns()}
                    dataSource={filteredAppList}
                    loading={loading}
                    pagination={{
                        total: filteredAppList.length,
                        showTotal: (total) => `${t('sdk.total') || '共'} ${total} ${t('sdk.apps') || '个应用'}`,
                        showSizeChanger: !isMobile,
                        pageSizeOptions: ['10', '20', '50'],
                        size: isMobile ? 'small' : 'default',
                    }}
                    rowKey="key"
                    size={isMobile ? 'small' : 'middle'}
                    // 移动端不设置 scroll，让表格自适应宽度
                    scroll={isMobile ? undefined : undefined}
                />
            </>
        );
    }

    renderCreateAppBtn() {
        return null;
    }
}

export default ApiKeyContent;
export { ApiKeyContent };